// Tiny fetch wrapper. Every endpoint is same-origin under /api/*; the
// Pages Functions middleware enforces auth and forwards to D1.

const LOGIN_PATH = '/__tt/login'

// Public, unauthenticated endpoints. A 401 here means a bad/expired survey
// token — NOT an expired staff session — so we must NOT bounce a public
// respondent to the staff login page.
function isPublicPath(path) {
  return path.startsWith('/api/survey/') || path.startsWith('/api/webhook/')
}

// Are we currently on a public survey page? Used as a second guard so an
// auth failure never yanks a patient/respondent into the staff login.
function onPublicPage() {
  return /^\/survey\//.test(window.location.pathname)
}

// Hard-redirect the browser to the login page, preserving where we were so the
// middleware can send the user back after a successful re-login. Guarded so we
// only ever fire one navigation even if many in-flight requests 401 at once.
let redirecting = false
export function forceLogin() {
  if (redirecting || onPublicPage()) return
  redirecting = true
  const here = window.location.pathname + window.location.search
  window.location.assign(`${LOGIN_PATH}?return=${encodeURIComponent(here)}`)
}

async function request(path, opts = {}) {
  let r
  try {
    r = await fetch(path, {
      headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
      credentials: 'include',
      ...opts,
    })
  } catch (err) {
    // Network-level failure (offline, proxy/backend down). Surface a readable
    // error rather than a raw "Failed to fetch".
    throw new Error(`network error: ${err?.message || err}`)
  }
  // 401 = session cookie missing/expired OR the Xano auth token inside it
  // expired (downstream proxy functions map upstream Xano 401 → 401). Either
  // way the user must re-authenticate → force them to the login page.
  if (r.status === 401 && !isPublicPath(path)) {
    forceLogin()
    throw new Error('unauthorized — redirecting to login')
  }
  if (!r.ok) {
    const text = await r.text().catch(() => '')
    // Every endpoint here fails through jsonError({ error }), and that string is
    // written to be read by staff. Dumping the envelope around it produced the
    // likes of `502 : {"error":"LINE rejected the message (429)"}` in the inbox
    // — the status is empty over HTTP/2, so the prefix was pure noise sitting in
    // front of the only part that meant anything. Unwrap it when it is there.
    let message = ''
    try { message = JSON.parse(text)?.error || '' } catch { /* not our envelope */ }
    throw new Error(message || `${r.status} ${r.statusText || 'error'}: ${text.slice(0, 200)}`)
  }
  return r.json()
}

function qs(obj) {
  const u = new URLSearchParams()
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length) u.set(k, v.join(','))
    } else {
      u.set(k, String(v))
    }
  }
  const s = u.toString()
  return s ? `?${s}` : ''
}

export const api = {
  // Chatbot inbox. Replying flips the conversation to 'human' server-side —
  // see functions/api/bot/conversations/[id]/reply.js for why that is the rule.
  bot: {
    list:   (params = {}) => request('/api/bot/conversations' + qs(params)),
    get:    (id)          => request(`/api/bot/conversations/${id}`),
    // Staff notifications. These were LINE pushes until 2026-09-21 — see
    // functions/api/bot/alerts.js for why they are not any more.
    alerts:     (params = {}) => request('/api/bot/alerts' + qs(params)),
    alertsRead: ()            => request('/api/bot/alerts', { method: 'POST', body: JSON.stringify({ read: true }) }),
    reply:  (id, text)    => request(`/api/bot/conversations/${id}/reply`, { method: 'POST', body: JSON.stringify({ text }) }),
    status: (id, status)  => request(`/api/bot/conversations/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    ignore: (id, ignored) => request(`/api/bot/conversations/${id}`, { method: 'PATCH', body: JSON.stringify({ ignored }) }),
    // body: { text?, image_key?, image?, image_name?, file? } — text and at most
    // ONE attachment. `image_key` is a saved reply's image; `image` is a data URI
    // (JPEG/PNG) sent as a real LINE picture; `file` is { data, filename } and
    // reaches the customer as a download link, because LINE has no file message.
    send:   (id, body)    => request(`/api/bot/conversations/${id}/reply`, { method: 'POST', body: JSON.stringify(body) }),

    // The sticker library. `send` above takes { sticker_id } to use one — it
    // sends the ONE stored copy, so no upload happens and no file is duplicated.
    stickers: {
      list:   (botOnly)   => request(`/api/bot/stickers${botOnly ? '?bot=1' : ''}`),
      create: (body)      => request('/api/bot/stickers', { method: 'POST', body: JSON.stringify(body) }),
      update: (id, patch) => request(`/api/bot/stickers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      remove: (id)        => request(`/api/bot/stickers/${id}`, { method: 'DELETE' }),
    },

    saved: {
      list:   ()          => request('/api/bot/saved-replies'),
      create: (body)      => request('/api/bot/saved-replies', { method: 'POST', body: JSON.stringify(body) }),
      update: (id, patch) => request(`/api/bot/saved-replies/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      remove: (id)        => request(`/api/bot/saved-replies/${id}`, { method: 'DELETE' }),
    },
  },

  leads: {
    list:   (params = {}) => request('/api/leads' + qs(params)),
    get:    (id)          => request(`/api/leads/${id}`),
    create: (body)        => request('/api/leads', { method: 'POST', body: JSON.stringify(body) }),
    update: (id, patch)   => request(`/api/leads/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    remove: (id)          => request(`/api/leads/${id}`, { method: 'DELETE' }),
    stats:  (period = 7) => request(`/api/leads/stats?period=${period}`),
  },
  companies: {
    // Accepts either a plain search string (legacy) or a full params object
    // { q, province: [...], only_with_ops: 1 }
    list:   (params = '')  => {
      if (typeof params === 'string') return request(`/api/companies${params ? `?q=${encodeURIComponent(params)}` : ''}`)
      return request('/api/companies' + qs(params))
    },
    get:    (id)          => request(`/api/companies/${id}`),
    create: (body)        => request('/api/companies', { method: 'POST', body: JSON.stringify(body) }),
    update: (id, patch)   => request(`/api/companies/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    remove: (id)          => request(`/api/companies/${id}`, { method: 'DELETE' }),

    // Nested contact endpoints — see functions/api/companies/[id]/contacts/*
    contacts: {
      add:    (companyId, body)            => request(`/api/companies/${companyId}/contacts`,      { method: 'POST',   body: JSON.stringify(body) }),
      update: (companyId, contactId, body) => request(`/api/companies/${companyId}/contacts/${contactId}`, { method: 'PATCH',  body: JSON.stringify(body) }),
      remove: (companyId, contactId)       => request(`/api/companies/${companyId}/contacts/${contactId}`, { method: 'DELETE' }),
    },

    logo: {
      get:    (companyId)          => request(`/api/companies/${companyId}/logo`),
      save:   (companyId, dataUri) => request(`/api/companies/${companyId}/logo`, { method: 'PUT',    body: JSON.stringify({ data: dataUri }) }),
      remove: (companyId)          => request(`/api/companies/${companyId}/logo`, { method: 'DELETE' }),
    },
  },
  followUps: {
    list:   (params = {}) => request('/api/follow_ups' + qs(params)),
    get:    (id)          => request(`/api/follow_ups/${id}`),
    create: (body)        => request('/api/follow_ups', { method: 'POST', body: JSON.stringify(body) }),
    update: (id, patch)   => request(`/api/follow_ups/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    remove: (id)          => request(`/api/follow_ups/${id}`, { method: 'DELETE' }),
    stats:  (period = 7) => request(`/api/follow_ups/stats?period=${period}`),
  },
  operations: {
    list:   (params = {}) => request('/api/operations' + qs(params)),
    get:    (id)          => request(`/api/operations/${id}`),
    create: (body)        => request('/api/operations', { method: 'POST', body: JSON.stringify(body) }),
    update: (id, patch)   => request(`/api/operations/${id}`, { method: 'PATCH',  body: JSON.stringify(patch) }),
    remove: (id)          => request(`/api/operations/${id}`, { method: 'DELETE' }),
    stats:  (period = 7) => request(`/api/operations/stats?period=${period}`),
    // Unique patients treated during this op (from Xano); also caches the count
    // onto the operation so the public HR report can read it.
    treatedCount: (id)   => request(`/api/operations/${id}/treated-count`),
    // Aggregated oral-health summary (from Xano chart.oral_exam); anonymized +
    // snapshotted onto the operation for the public HR report.
    oralHealth:   (id)   => request(`/api/operations/${id}/oral-health`),

    contacts: {
      add:    (opId, body)              => request(`/api/operations/${opId}/contacts`,        { method: 'POST',   body: JSON.stringify(body) }),
      update: (opId, cId, body)         => request(`/api/operations/${opId}/contacts/${cId}`, { method: 'PATCH',  body: JSON.stringify(body) }),
      remove: (opId, cId)               => request(`/api/operations/${opId}/contacts/${cId}`, { method: 'DELETE' }),
    },

    // Per-operation survey state. Each operation has 0..1 registration token
    // and 0..1 CST token. Tokens are generated / revoked from the Overview.
    surveys: {
      summary:                (id) => request(`/api/operations/${id}/surveys`),
      // Toggle whether the public registration page shows the services card.
      setShowServices:        (id, show) => request(`/api/operations/${id}/surveys`, { method: 'PATCH', body: JSON.stringify({ show_services: !!show }) }),
      generateRegistration:   (id) => request(`/api/operations/${id}/surveys/registration/token`, { method: 'POST' }),
      revokeRegistration:     (id) => request(`/api/operations/${id}/surveys/registration/token`, { method: 'DELETE' }),
      generateCst:            (id) => request(`/api/operations/${id}/surveys/cst/token`,          { method: 'POST' }),
      revokeCst:              (id) => request(`/api/operations/${id}/surveys/cst/token`,          { method: 'DELETE' }),
      generateReport:         (id) => request(`/api/operations/${id}/surveys/report/token`,       { method: 'POST' }),
      revokeReport:           (id) => request(`/api/operations/${id}/surveys/report/token`,       { method: 'DELETE' }),
      registrations:          (id) => request(`/api/operations/${id}/surveys/registration/responses`),
      updateRegistration:     (id, rid, body) => request(`/api/operations/${id}/surveys/registration/responses/${rid}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteRegistration:     (id, rid)       => request(`/api/operations/${id}/surveys/registration/responses/${rid}`, { method: 'DELETE' }),
      cstResponses:           (id) => request(`/api/operations/${id}/surveys/cst/responses`),
      // Per-row hide flag — drops a CST response from the HR-facing report
      // without deleting the underlying score data.
      updateCstResponse:      (id, rid, body) => request(`/api/operations/${id}/surveys/cst/responses/${rid}`, { method: 'PATCH', body: JSON.stringify(body) }),
      // Permanently delete a single CST response (score + comment) everywhere.
      deleteCstResponse:      (id, rid)       => request(`/api/operations/${id}/surveys/cst/responses/${rid}`, { method: 'DELETE' }),
    },

    prImage: {
      get:    (id)          => request(`/api/operations/${id}/pr-image`),
      save:   (id, dataUri) => request(`/api/operations/${id}/pr-image`, { method: 'PUT',    body: JSON.stringify({ data: dataUri }) }),
      remove: (id)          => request(`/api/operations/${id}/pr-image`, { method: 'DELETE' }),
    },

    prMainSq: {
      get:    (id)          => request(`/api/operations/${id}/pr-main-sq`),
      save:   (id, dataUri) => request(`/api/operations/${id}/pr-main-sq`, { method: 'PUT',    body: JSON.stringify({ data: dataUri }) }),
      remove: (id)          => request(`/api/operations/${id}/pr-main-sq`, { method: 'DELETE' }),
    },

    prMainA4: {
      get:    (id)          => request(`/api/operations/${id}/pr-main-a4`),
      save:   (id, dataUri) => request(`/api/operations/${id}/pr-main-a4`, { method: 'PUT',    body: JSON.stringify({ data: dataUri }) }),
      remove: (id)          => request(`/api/operations/${id}/pr-main-a4`, { method: 'DELETE' }),
    },

    // CST satisfaction-survey poster — staff-only, never exposed to HR.
    cstImage: {
      get:    (id)          => request(`/api/operations/${id}/cst-image`),
      save:   (id, dataUri) => request(`/api/operations/${id}/cst-image`, { method: 'PUT',    body: JSON.stringify({ data: dataUri }) }),
      remove: (id)          => request(`/api/operations/${id}/cst-image`, { method: 'DELETE' }),
    },

    // Event-report photo gallery — multiple images per operation. Uploaded
    // one-at-a-time from the Report tab; HR sees them in the public report.
    reportPhotos: {
      list:   (opId)             => request(`/api/operations/${opId}/report-photos`),
      upload: (opId, body)       => request(`/api/operations/${opId}/report-photos`, { method: 'POST',   body: JSON.stringify(body) }),
      remove: (opId, photoId)    => request(`/api/operations/${opId}/report-photos/${photoId}`, { method: 'DELETE' }),
      update: (opId, photoId, body) => request(`/api/operations/${opId}/report-photos/${photoId}`, { method: 'PATCH',  body: JSON.stringify(body) }),
      zipUrl: (opId)             => `/api/operations/${opId}/report-photos/zip`,
    },
  },

  // SSDC treatment dashboard feed — per-day patient treatments at the Tooth
  // Taxi branch (branch_id fixed to 9 server-side). Proxies Xano /treatment.
  // Params: { date, page, per_page, doctor_id }.
  treatment: {
    list: (params = {}) => request('/api/treatment' + qs(params)),
  },

  // Per-day branch-9 revenue aggregate (Total + Clinic) from Xano chart/revenue.
  // One fast call for a date range → { days: [{ date, total, clinic }] }.
  revenue: {
    range: (start, end) => request(`/api/revenue?start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`),
  },

  // Admin: top-level CST score overview, sorted/filterable.
  cst: {
    list:      (params = {}) => request('/api/cst' + qs(params)),
    responses: (params = {}) => request('/api/cst/responses' + qs(params)),
  },

  // Image-editor template layouts (admin-tunable defaults).
  imageTemplates: {
    list:    ()             => request('/api/image-templates'),
    saveOne: (key, slots)   => request(`/api/image-templates/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ slots }) }),
  },

  // Document workflow — catalog of doc kinds + per-operation requests + files.
  documentKinds: {
    list:        ()            => request('/api/document-kinds'),
    create:      (body)        => request('/api/document-kinds', { method: 'POST', body: JSON.stringify(body) }),
    favorite:    (id)          => request(`/api/document-kinds/${id}/favorite`, { method: 'PUT' }),
    unfavorite:  (id)          => request(`/api/document-kinds/${id}/favorite`, { method: 'DELETE' }),
  },
  operationDocuments: {
    list:        (opId)                       => request(`/api/operations/${opId}/documents`),
    addRequest:  (opId, body)                 => request(`/api/operations/${opId}/documents`,            { method: 'POST',   body: JSON.stringify(body) }),
    updateReq:   (opId, reqId, body)          => request(`/api/operations/${opId}/documents/${reqId}`,   { method: 'PATCH',  body: JSON.stringify(body) }),
    removeReq:   (opId, reqId)                => request(`/api/operations/${opId}/documents/${reqId}`,   { method: 'DELETE' }),
    uploadFile:  (opId, reqId, body)          => request(`/api/operations/${opId}/documents/${reqId}/files`, { method: 'POST', body: JSON.stringify(body) }),
    getFile:     (opId, reqId, fileId)        => request(`/api/operations/${opId}/documents/${reqId}/files/${fileId}`),
    // Soft-delete (default) OR permanent delete ({ permanent: true }).
    deleteFile:  (opId, reqId, fileId, { reason, permanent } = {}) => {
      const params = new URLSearchParams()
      if (permanent) params.set('permanent', '1')
      if (reason)    params.set('reason', reason)
      const qs = params.toString() ? `?${params}` : ''
      return request(`/api/operations/${opId}/documents/${reqId}/files/${fileId}${qs}`, { method: 'DELETE' })
    },
    restoreFile: (opId, reqId, fileId) =>
      request(`/api/operations/${opId}/documents/${reqId}/files/${fileId}`, { method: 'PUT' }),
    generate:    (opId, reqId, body) =>
      request(`/api/operations/${opId}/documents/${reqId}/generate`, { method: 'POST', body: JSON.stringify(body) }),
  },

  // Per-staff list of previously-used authorized representatives — used by the
  // "หนังสือมอบอำนาจ (สสจ.)" generate popup so staff can pick + edit instead of
  // re-typing the same rep on every operation.
  staffAuthorizeReps: {
    list:   ()          => request('/api/staff/authorize-reps'),
    upsert: (body)      => request('/api/staff/authorize-reps', { method: 'POST', body: JSON.stringify(body) }),
    remove: (id)        => request(`/api/staff/authorize-reps?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },

  // Current user's Xano employee record (prefix/name/surname). Used to
  // pre-fill witness1 in the "หนังสือมอบอำนาจ" generate popup.
  staffMeEmployee: () => request('/api/staff/me-employee'),

  // Static reference PDFs that appear in the SSO sections of the operation
  // Documents tab. Each entry groups 1-2 document_kind rows (one per section
  // it's assigned to) under one logical "reference".
  staticRefs: {
    list:   ()          => request('/api/static-refs'),
    create: (body)      => request('/api/static-refs', { method: 'POST', body: JSON.stringify(body) }),
    update: (key, body) => request(`/api/static-refs?key=${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (key)      => request(`/api/static-refs?key=${encodeURIComponent(key)}`, { method: 'DELETE' }),
  },

  // Referral contacts — saved per-province hospital / referral unit records.
  // Used by the "ใบประกาศบนรถ (ประจำวัน)" generate popup.
  referralContacts: {
    list:   (province)     => request('/api/referral-contacts' + (province ? `?province=${encodeURIComponent(province)}` : '')),
    create: (body)         => request('/api/referral-contacts',       { method: 'POST',   body: JSON.stringify(body) }),
    update: (id, body)     => request(`/api/referral-contacts/${id}`, { method: 'PUT',    body: JSON.stringify(body) }),
    remove: (id)           => request(`/api/referral-contacts/${id}`, { method: 'DELETE' }),
  },

  // Global catalog of "other" mobile dental units — used by the
  // "หนังสือขอปฎิเสธจากรถทันตกรรมอื่นๆ" multi-bus generate popup.
  // List can be filtered by SSO district so the picker only shows buses
  // registered in the same area as the current operation.
  otherMobileDentals: {
    list:   (ssoDistrict)        => request('/api/other-mobile-dentals' + (ssoDistrict ? `?sso_district=${encodeURIComponent(ssoDistrict)}` : '')),
    upsert: (body)               => request('/api/other-mobile-dentals',                                       { method: 'POST',   body: JSON.stringify(body) }),
    update: (id, body)           => request(`/api/other-mobile-dentals?id=${encodeURIComponent(id)}`,           { method: 'PATCH',  body: JSON.stringify(body) }),
    remove: (id)                 => request(`/api/other-mobile-dentals?id=${encodeURIComponent(id)}`,           { method: 'DELETE' }),
  },

  // Public — used by /survey/registration/:token and /survey/cst/:token; no auth required.
  publicSurvey: {
    registration: {
      get:    (token)       => request(`/api/survey/registration/${encodeURIComponent(token)}`),
      submit: (token, body) => request(`/api/survey/registration/${encodeURIComponent(token)}/submit`, { method: 'POST', body: JSON.stringify(body) }),
    },
    cst: {
      get:    (token)       => request(`/api/survey/cst/${encodeURIComponent(token)}`),
      submit: (token, body) => request(`/api/survey/cst/${encodeURIComponent(token)}/submit`, { method: 'POST', body: JSON.stringify(body) }),
    },
    report: {
      get:    (token)                          => request(`/api/survey/report/${encodeURIComponent(token)}`),
      uploadFile: (token, reqId, body)         => request(`/api/survey/report/${encodeURIComponent(token)}/documents/${reqId}/files`, { method: 'POST', body: JSON.stringify(body) }),
      deleteFile: (token, reqId, fileId, reason) => {
        const qs = reason ? `?reason=${encodeURIComponent(reason)}` : ''
        return request(`/api/survey/report/${encodeURIComponent(token)}/documents/${reqId}/files/${fileId}${qs}`, { method: 'DELETE' })
      },
      // Same-origin proxy URL for a single uploaded file — feed this to <iframe>
      // or PDF.js so Arc Shields / ad-blockers don't trip on the Wasabi origin.
      filePreviewUrl: (token, reqId, fileId) =>
        `/api/survey/report/${encodeURIComponent(token)}/documents/${reqId}/files/${fileId}/preview`,
      // Triggers a server-side ZIP of every generated doc for this report token.
      zipGeneratedUrl: (token) =>
        `/api/survey/report/${encodeURIComponent(token)}/zip-generated`,
      // Triggers a server-side ZIP of every event-report photo for this token.
      photosZipUrl: (token) =>
        `/api/survey/report/${encodeURIComponent(token)}/photos-zip`,
    },
  },
}
